import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

import { CARD_ELEMENT_TYPE } from '../../../../../../../services/cards/constants';
import Label from '../../../../../Label';
import Button from '../../../../../Button';
import {
  FONT_FAMILY,
  TEXT_ALIGN,
  TEXT_PRESETS,
  googleFontInfo,
} from '../../../CardDesignerCardElements/CardDesignerCardElementText/helpers';
import InfoCircleIcon from '../../../../../../icons/InfoCircleIcon';
import GoogleFontLoader from '../../../../../../../services/cards/components/CardElementText/GoogleFontLoader';

const propTypes = {
  className: PropTypes.string,
  onCardElementCreate: PropTypes.func.isRequired,
  zElementIndexMax: PropTypes.number,
  lastUsedFont: PropTypes.oneOf(Object.values(FONT_FAMILY)),
};

const CardDesignerTextControls = ({
  className,
  onCardElementCreate,
  zElementIndexMax,
  lastUsedFont,
}) => {
  const classes = classNames({
    [className]: !!className,
  });

  const googleFontDetails = googleFontInfo.find(x => x.font === lastUsedFont);

  const textBoxes = useMemo(
    () =>
      Object.values(TEXT_PRESETS).map(preset => ({
        label: preset,
        font_size: '16px',
        cardElementConfiguration: {
          help_placeholder: preset,
          is_editable: true,
          text_align: TEXT_ALIGN.CENTER,
          font_size: 0.08,
        },
        input: {
          frame: {
            x: 0.1,
            y: 0.25,
            width: 0.8,
            height: 0.05,
          },
        },
      })),
    [],
  );

  return (
    <div className={classes}>
      <div className="card-designer-layout-text-controls">
        <Label id="text-control">Add text</Label>
        <Button
          btnBlock
          className="mb-4"
          onClick={() => {
            onCardElementCreate({
              cardElementConfiguration: {
                is_editable: true,
                font_family: lastUsedFont,
              },
              cardElementType: CARD_ELEMENT_TYPE.TEXT,
              input: {
                frame: {
                  x: 0.08,
                  y: 0.08,
                  width: 0.44,
                  height: 0.06,
                },
                z_index: zElementIndexMax ?? 2,
              },
            });
          }}
        >
          Add a text box
        </Button>
        <Label id="text-presets-control">
          Text Presets{' '}
          <InfoCircleIcon
            size="12"
            data-tip=""
            data-for="text-preset-tip"
            data-event="mouseenter click"
            data-effect="solid"
            data-place="bottom"
          />
          <ReactTooltip id="text-preset-tip" className="tooltip">
            Pre-sets are automatically filled with the appropriate data when the
            SmartMate is configured by a purchaser. If matching data is not
            found, the preset is cleared and hidden.
          </ReactTooltip>
        </Label>
        {textBoxes.map(textBox => {
          return (
            <Button
              btnStyle="reset"
              className="card-designer-layout-text-controls-button"
              onClick={() => {
                onCardElementCreate({
                  cardElementConfiguration: {
                    is_editable: true,
                    ...textBox.cardElementConfiguration,
                  },
                  cardElementType: CARD_ELEMENT_TYPE.TEXT,
                  input: {
                    ...textBox.input,
                    z_index: zElementIndexMax ?? 2,
                  },
                });
              }}
              style={{
                fontFamily:
                  googleFontDetails?.fontFamily ??
                  textBox.cardElementConfiguration.font_family,
                fontWeight: textBox.cardElementConfiguration.font_weight,
                textTransform: textBox.cardElementConfiguration.text_transform,
                fontSize: textBox.font_size,
              }}
            >
              {textBox.label}
            </Button>
          );
        })}
        {googleFontDetails && <GoogleFontLoader fontFamily={lastUsedFont} />}
      </div>
    </div>
  );
};

CardDesignerTextControls.propTypes = propTypes;

export default CardDesignerTextControls;
